import React, { useState, useEffect } from 'react';
import { X, Sparkles, Search, Filter, Trash2, Edit2, User } from 'lucide-react';
import { ArchitectureTemplate } from '../types';
import { ARCHITECTURE_TEMPLATES } from '../templates/architectureTemplates';
import { getUserTemplates, deleteUserTemplate } from '../utils/templateStorage';

interface TemplateGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: ArchitectureTemplate) => void;
  onEditTemplate?: (template: ArchitectureTemplate) => void;
}

const categoryColors = {
  web: 'bg-blue-100 text-blue-800 border-blue-300',
  microservices: 'bg-purple-100 text-purple-800 border-purple-300',
  serverless: 'bg-green-100 text-green-800 border-green-300',
  data: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  kubernetes: 'bg-indigo-100 text-indigo-800 border-indigo-300'
};

const difficultyColors = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-red-100 text-red-700'
};

const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  onEditTemplate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [userTemplates, setUserTemplates] = useState<ArchitectureTemplate[]>([]);
  const [showUserTemplates, setShowUserTemplates] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setUserTemplates(getUserTemplates());
    }
  }, [isOpen]);

  const allTemplates = [...ARCHITECTURE_TEMPLATES, ...userTemplates];
  
  const filteredTemplates = allTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesType = showUserTemplates || !template.id.startsWith('user_');
    return matchesSearch && matchesCategory && matchesType;
  });

  const categories = ['all', ...Array.from(new Set(ARCHITECTURE_TEMPLATES.map(t => t.category)))];

  const handleSelectTemplate = (template: ArchitectureTemplate) => {
    if (window.confirm(`Load template "${template.name}"? This will replace your current canvas.`)) {
      onSelectTemplate(template);
      onClose();
    }
  };

  const handleDeleteTemplate = (template: ArchitectureTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Delete template "${template.name}"?`)) {
      if (deleteUserTemplate(template.id)) {
        setUserTemplates(getUserTemplates());
      }
    }
  };

  const handleEditTemplate = (template: ArchitectureTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEditTemplate) {
      onEditTemplate(template);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-2xl w-[900px] max-w-[90vw] max-h-[85vh] flex flex-col border-2 border-black"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-black/10 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Sparkles size={24} className="text-purple-600" />
            <div>
              <h2 className="text-xl font-bold text-black">Architecture Templates</h2>
              <p className="text-sm text-gray-600 mt-1">Start with a proven architecture pattern</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-black/10 space-y-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={16} className="text-gray-500" />
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`
                  px-3 py-1 rounded-full text-xs font-medium transition-all capitalize
                  ${selectedCategory === cat 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {cat}
              </button>
            ))}
            <div className="ml-auto">
              <button
                onClick={() => setShowUserTemplates(!showUserTemplates)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  showUserTemplates 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <User size={12} className="inline mr-1" />
                My Templates
              </button>
            </div>
          </div>
        </div>

        {/* Template Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Sparkles size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg">No templates found</p>
              <p className="text-sm mt-2">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer transition-all hover:border-purple-500 hover:shadow-lg group"
                >
                  {/* Template Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-black group-hover:text-purple-600 transition-colors">
                          {template.name}
                        </h3>
                        {template.id.startsWith('user_') && (
                          <div title="User Template">
                            <User size={14} className="text-purple-600" />
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {template.description}
                      </p>
                    </div>
                    {template.id.startsWith('user_') && (
                      <div className="flex items-center gap-1">
                        {onEditTemplate && (
                          <button
                            onClick={(e) => handleEditTemplate(template, e)}
                            className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                            title="Edit Template"
                          >
                            <Edit2 size={14} />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDeleteTemplate(template, e)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete Template"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${categoryColors[template.category]}`}>
                      {template.category}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${difficultyColors[template.difficulty]}`}>
                      {template.difficulty}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{template.nodes.length} components</span>
                    <span>{template.edges.length} connections</span>
                  </div>

                  {/* Visual Indicator */}
                  <div className="mt-3 h-20 bg-gray-50 border border-gray-200 rounded flex items-center justify-center">
                    <div className="text-xs text-gray-400 font-medium">
                      Click to load template
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-black/10 bg-gray-50 text-xs text-gray-500 text-center">
          {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} available
        </div>
      </div>
    </div>
  );
};

export default TemplateGallery;
